import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const SUCCESS_MESSAGE_DISPLAY_DURATION = 2000;

/**
 * Tracks every `.bundle-item-input` radio inside it (one per bundle-product card, always
 * exactly one checked per card) to compute a live subtotal, and adds every checked, enabled
 * variant to the cart in a single request when "Add all to cart" is pressed.
 *
 * Mirrors the add-to-cart request/event contract in `@theme/product-form` (same
 * `Theme.routes.cart_add_url` payload shape, same `CartLinesUpdateEvent`/`CartErrorEvent`
 * dispatch) so the existing cart drawer, cart count bubble, and open-on-add behavior all work
 * without any bundle-specific wiring on their end.
 *
 * @typedef {object} BundleBuilderRefs
 * @property {HTMLButtonElement} addButton
 * @property {HTMLElement} subtotal
 * @property {HTMLElement} itemCount
 * @property {HTMLElement} [errorText]
 *
 * @extends Component<BundleBuilderRefs>
 */
class BundleBuilderComponent extends Component {
  requiredRefs = ['addButton', 'subtotal', 'itemCount'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#handleChange);
    this.#updateSummary();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.removeEventListener('change', this.#handleChange);
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.classList.contains('bundle-item-input')) {
      this.#updateSummary();
    }
  };

  /** @returns {HTMLInputElement[]} the checked, addable radio in every bundle-product card */
  get #selectedInputs() {
    return /** @type {HTMLInputElement[]} */ (
      Array.from(this.querySelectorAll('.bundle-item-input:checked')).filter(
        (input) => input instanceof HTMLInputElement && !input.disabled
      )
    );
  }

  #updateSummary() {
    const inputs = this.#selectedInputs;
    const total = inputs.reduce((sum, input) => sum + (parseInt(input.dataset.price ?? '', 10) || 0), 0);
    const currency = this.dataset.currency ?? 'USD';
    const format = this.dataset.moneyFormat ?? '${{amount}}';

    this.refs.subtotal.textContent = formatMoney(total, format, currency);
    this.refs.itemCount.textContent = String(inputs.length);
    this.refs.addButton.disabled = inputs.length === 0;
  }

  /** @param {Event} event */
  handleAddAll = async (event) => {
    event.preventDefault();

    const inputs = this.#selectedInputs;
    if (inputs.length === 0) return;

    const { addButton } = this.refs;
    const originalLabel = addButton.textContent;
    addButton.disabled = true;
    addButton.setAttribute('aria-busy', 'true');
    if (this.refs.errorText) this.refs.errorText.hidden = true;

    const items = inputs.map((input) => ({ id: Number(input.value), quantity: 1 }));

    const cartItemsComponent = document.querySelector('cart-items-component');
    const sectionIds = [];
    if (cartItemsComponent instanceof HTMLElement && cartItemsComponent.dataset.sectionId) {
      sectionIds.push(cartItemsComponent.dataset.sectionId);
    }

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();

      if (result.status) {
        throw new Error(result.message || 'Add to cart failed');
      }

      const cart = await this.#refreshCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'bundle-builder-component',
          sourceId: this.id?.toString() ?? '',
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
          sections: result.sections,
          didError: false,
        },
      });

      addButton.textContent = Theme.translations?.added ?? 'Added';
      setTimeout(() => {
        addButton.textContent = originalLabel;
      }, SUCCESS_MESSAGE_DISPLAY_DURATION);
    } catch (error) {
      console.error(error);

      const message = error instanceof Error ? error.message : 'Add to cart failed';

      this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
      deferredEventPromise.reject(error);

      if (this.refs.errorText) {
        this.refs.errorText.textContent = message;
        this.refs.errorText.hidden = false;
      }

      addButton.textContent = originalLabel;
    } finally {
      addButton.disabled = false;
      addButton.removeAttribute('aria-busy');
    }
  };

  /** Refreshes the cart drawer's line items, matching `@theme/product-form`'s fallback chain. */
  async #refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return /** @type {{fetchCartData: () => Promise<any>}} */ (cartItemsComponent).fetchCartData();
    }

    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }
}

if (!customElements.get('bundle-builder-component')) {
  customElements.define('bundle-builder-component', BundleBuilderComponent);
}
